import { TriangleAlert } from "lucide-react";
import { formatMoney, sumMoney } from "@empite/api-client";
import type {
  Alert,
  AlertMeasure,
  Money,
  ReservationStatus,
  SubscriptionStatus,
  TopUpStatus,
  UsageRecord,
} from "@empite/api-client";
import type { AppIdentity } from "../ui/app-identity";
import { Button } from "../ui/button";
import { formatCount, formatDate, humanizeState } from "../ui/format";
import { Notice } from "../ui/notice";
import { outcomeTone } from "../ui/status-pill";
import type { UsagePoint, UsageSeries } from "../ui/usage-chart";
import type { PortalApplication } from "../applications";

/** The pill tones, named from the component that owns them rather than
 *  re-declared here — `status-pill.tsx` keeps its `Tone` union private. */
type PillTone = ReturnType<typeof outcomeTone>;

/**
 * A `PortalApplication` as the ported components want to be handed it.
 *
 * The design's fixture carried an initial and a colour per application; the
 * platform's list carries neither, so both are derived here rather than in
 * `src/ui/` — the kit is given an identity, it never computes one. The tone
 * cycles by position for the same reason the chart's does: a palette keyed on
 * an opaque id would look random, and keyed on nothing would make every icon
 * the same colour.
 */
export function appIdentity(application: PortalApplication, index = 0): AppIdentity {
  const name = application.name || application.slug;
  return {
    id: application.id,
    name,
    slug: application.slug,
    initial: (name.trim()[0] ?? "?").toUpperCase(),
    tone: index % 2 === 0 ? "brand" : "teal",
  };
}

/**
 * Several currency totals as one string — the same list `Totals` renders, for
 * the ported components that take a plain value rather than a node.
 *
 * It joins rather than adds, because {@link sumByCurrency} deliberately does
 * not collapse two currencies into one number and neither may its display.
 */
export function formatTotals(amounts: Money[], empty = "—"): string {
  if (amounts.length === 0) {
    return empty;
  }
  // Not `amounts.map(formatMoney)`: map hands the callback an index, which
  // formatMoney reads as `fractionDigits` — the first total would render whole
  // (`USD 0`) and the second to one decimal place.
  return amounts.map((amount) => formatMoney(amount)).join(" + ");
}

/** The window the usage chart covers, in days. The usage lists are capped per
 *  application, so this is the widest honest axis, not a query parameter. */
export const CHART_DAYS = 30;

const DAY_MS = 86_400_000;

/** One application's records, as the chart helpers below want them. */
export interface ChartInput {
  application: PortalApplication;
  records: UsageRecord[];
}

/** A line per application, coloured by POSITION for the same reason
 *  {@link appIdentity}'s tone is. */
export function chartSeries(rows: ChartInput[]): UsageSeries[] {
  return rows.map((row, index) => ({
    key: row.application.id,
    label: row.application.name,
    tone: index % 2 === 0 ? ("brand" as const) : ("teal" as const),
  }));
}

/**
 * Requests per application per day, bucketed in the browser from the pages
 * each application already answered.
 *
 * There is no route that returns a series, and adding one would have to cross
 * a wallet boundary to compute it. So the axis is the last {@link CHART_DAYS}
 * days and the counts come from the records on hand — which are capped per
 * application, making this a picture of recent activity rather than a
 * complete tally. The totals beside it come from `summary`, which is not
 * capped, and that is the number to trust.
 *
 * Records are bucketed on the DATE PART of their own timestamp, exactly as
 * `formatDayMonth` renders it, so a column's label and its contents cannot
 * disagree about which day they mean.
 *
 * It lives here rather than on either screen because /overview and /usage draw
 * the SAME chart: two copies of this bucketing would eventually disagree about
 * what a day is, and the two pictures would differ for no reason a reader
 * could see.
 */
export function chartPoints(rows: ChartInput[], now = Date.now()): UsagePoint[] {
  const midnight = new Date(now);
  const end = Date.UTC(
    midnight.getUTCFullYear(),
    midnight.getUTCMonth(),
    midnight.getUTCDate(),
  );

  const byDay = new Map<string, Record<string, number>>();
  for (let index = 0; index < CHART_DAYS; index++) {
    const day = new Date(end - (CHART_DAYS - 1 - index) * DAY_MS).toISOString();
    byDay.set(day.slice(0, 10), {});
  }

  for (const row of rows) {
    for (const record of row.records) {
      const values = byDay.get(record.created_at.slice(0, 10));
      if (!values) {
        continue;
      }
      values[row.application.id] = (values[row.application.id] ?? 0) + 1;
    }
  }

  return [...byDay.entries()].map(([day, values]) => ({
    date: `${day}T00:00:00Z`,
    values,
  }));
}

/**
 * Totals are kept PER CURRENCY and never collapsed into one number.
 *
 * Wallets are per application, and nothing says two applications price in the
 * same currency. Adding USD micros to GBP micros produces a figure that is
 * wrong in a way no reader can see, so a customer with wallets in two
 * currencies is shown two totals — which is also the honest answer, since
 * credit cannot move between them either.
 */
export function sumByCurrency(amounts: Array<Money | undefined>): Money[] {
  // Group first, then add each group with `sumMoney`, which is the one place
  // that knows micros are BigInt — and which throws if a group ever holds two
  // currencies, so a bug in this grouping surfaces instead of totalling.
  const groups = new Map<string, Money[]>();
  for (const amount of amounts) {
    if (!amount) {
      continue;
    }
    const group = groups.get(amount.currency);
    if (group) {
      group.push(amount);
    } else {
      groups.set(amount.currency, [amount]);
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([, group]) => {
      const total = sumMoney(group);
      return total ? [total] : [];
    });
}

/**
 * The same statuses as a design pill, for the ported tables.
 *
 * `status-pill.tsx:outcomeTone` deliberately knows only the generic outcome
 * vocabulary, and a payment has two states it has never heard of: `processing`
 * is money in flight and reads as a warning, `refunded` is a settled ending and
 * reads as neutral. Left to `outcomeTone` both would come out neutral, which
 * says "nothing is happening" about a payment that is still moving.
 */
export function topUpPillTone(status: TopUpStatus): PillTone {
  if (status === "processing") {
    return "warning";
  }
  if (status === "refunded" || status === "cancelled") {
    return "neutral";
  }
  return outcomeTone(status);
}

/**
 * A reservation's status as a design pill.
 *
 * `outcomeTone` has never heard of any of these four, so left to it every row
 * would read neutral — including `expired`, which is a hold that was never
 * settled and is the one row worth looking at. The mapping mirrors
 * {@link ReservationBadge}'s so a screen mid-port cannot show two different
 * colours for the same word.
 */
export function reservationPillTone(status: ReservationStatus): PillTone {
  if (status === "held") {
    return "warning";
  }
  if (status === "settled") {
    return "success";
  }
  if (status === "expired") {
    return "danger";
  }
  return "neutral";
}

/**
 * A wallet's status in the pill's vocabulary.
 *
 * `closed` is spelled out rather than left to `outcomeTone`, which would call
 * it neutral: a closed wallet cannot be topped up, and a status the customer
 * has to act on does not read as "nothing to see here".
 */
export function walletPillTone(status: string): PillTone {
  if (status === "closed") {
    return "danger";
  }
  return outcomeTone(status);
}

/**
 * A subscription's status as a design pill.
 *
 * `outcomeTone` knows `active` and nothing else here, so left to it the two
 * states a customer has to ACT on would come out the same grey as an
 * agreement that ended months ago. `past_due` is a refused renewal and reads
 * as danger; `incomplete` is a payment nobody has confirmed yet and reads as
 * the same warning every other pending thing in this portal does — which is
 * also the whole of "an incomplete subscription must not look active".
 *
 * `cancelled` and `expired` are deliberately neutral rather than danger: they
 * are finished agreements, and a red pill would make an ordinary ending look
 * like a fault.
 */
export function subscriptionPillTone(status: SubscriptionStatus): PillTone {
  if (status === "past_due") {
    return "danger";
  }
  if (status === "incomplete") {
    return "warning";
  }
  if (status === "cancelled" || status === "expired") {
    return "neutral";
  }
  return outcomeTone(status);
}


/**
 * An alert's severity as a design pill.
 *
 * `outcomeTone` has never heard of any of the three, so left to it a critical
 * "you are out of credit" row would read the same grey as everything else in
 * the table. `info` stays neutral on purpose: it is a crossing worth recording
 * and not one worth colouring, and spending the brand colour on it would leave
 * nothing louder for the two that need a customer to act.
 */
export function alertPillTone(severity: string): PillTone {
  if (severity === "critical") {
    return "danger";
  }
  if (severity === "warning") {
    return "warning";
  }
  return "neutral";
}

/** The same severity in the callout's vocabulary. `Notice` has no `neutral`,
 *  and its default tone is the informational blue. */
export function alertNoticeTone(severity: string): "danger" | "warning" | "info" {
  if (severity === "critical") {
    return "danger";
  }
  if (severity === "warning") {
    return "warning";
  }
  return "info";
}

/** Loudest first. Unknown severities sort last rather than being dropped — a
 *  crossing this build cannot name is still a crossing. */
const SEVERITY_RANK: Record<string, number> = { critical: 3, warning: 2, info: 1 };

/**
 * An alert with no `resolved_at` is still OPEN.
 *
 * The route serves resolved crossings too, so that the table can be a history.
 * Nothing that shouts at a customer may be drawn from that list unfiltered:
 * the alert is frozen as it fired, so a resolved one is a true statement about
 * a balance that has since been fixed.
 */
export function isOpenAlert(alert: Alert): boolean {
  return !alert.resolved_at;
}

/**
 * One side of a crossing, as text.
 *
 * `amount` is the ONLY route to a money figure here. A micro is a millionth of
 * a MINOR unit, so a browser that divides `value` by a million renders a
 * hundred times the real balance — a customer told they hold USD 250.00 when
 * the ledger says USD 2.50. When the platform attached no amount the unit is
 * not money at all, and the honest rendering is the figure beside the unit it
 * was measured in rather than a number this screen has decided what to call.
 */
export function formatMeasure(measure: AlertMeasure): string {
  if (measure.amount) {
    return formatMoney(measure.amount);
  }
  return `${formatCount(measure.value)} ${measure.unit}`;
}

/**
 * When this wallet is forecast to be empty, if the alert carried a forecast.
 *
 * `detail` is a free-form object the alert engine fills in, and this portal is
 * deployed separately from it: every key is checked for existence AND for
 * type, so an engine that renames a field or starts sending a number where a
 * string was makes this sentence disappear rather than render "Invalid Date"
 * at the top of the screen. A forecast is the part of the banner a customer
 * can least afford to have invented for them.
 */
export function alertForecast(detail: Record<string, unknown> | undefined): string | undefined {
  const at = detail?.["exhausts_at"];
  // Checked against the shape `formatDate` splits by hand, not `Date.parse`:
  // the helper never parses, so anything that is not RFC 3339 would come back
  // as a date assembled from the wrong pieces instead of failing.
  if (typeof at === "string" && /^\d{4}-\d{2}-\d{2}T/.test(at)) {
    return `Forecast to be empty on ${formatDate(at)}.`;
  }
  const days = detail?.["days_to_exhaustion"];
  if (typeof days === "number" && Number.isFinite(days) && days >= 0) {
    const whole = Math.trunc(days);
    return `Forecast to be empty in about ${formatCount(whole)} day${whole === 1 ? "" : "s"}.`;
  }
  return undefined;
}

/**
 * What crossed, in a customer's words.
 *
 * `detail.trigger` says which of the three thresholds was the binding one, and
 * the three mean genuinely different things to the person reading: two are
 * "your balance is below a line", the third is "your balance is fine and your
 * spending is not". An unrecognised trigger falls back to the alert's kind
 * rather than to a guess, because a wrong reason is worse than a vague one.
 */
export function alertReason(alert: Alert): string {
  const trigger = alert.detail?.["trigger"];
  if (typeof trigger === "string") {
    if (trigger === "floor") {
      return "Balance below the alert floor";
    }
    if (trigger === "override") {
      return "Balance below your account threshold";
    }
    if (trigger === "forecast") {
      return "Spending will empty this wallet";
    }
  }
  return humanizeState(alert.kind);
}

/** One alert with the application it was raised against. Both billing screens
 *  build these; only this file knows how to draw them. */
export interface WalletAlertEntry {
  /** `Alert.id` — DataTable keys its rows on this, and so does the banner. */
  id: string;
  alert: Alert;
  app: AppIdentity;
}

/** Open crossings, loudest first — the banner's input, and never the table's. */
export function openAlerts(entries: WalletAlertEntry[]): WalletAlertEntry[] {
  return entries
    .filter((entry) => isOpenAlert(entry.alert))
    .sort(
      (a, b) =>
        (SEVERITY_RANK[b.alert.severity] ?? 0) - (SEVERITY_RANK[a.alert.severity] ?? 0) ||
        b.alert.created_at.localeCompare(a.alert.created_at),
    );
}

/**
 * The open-alert banner, shared by the wallets grid and one wallet's screen.
 *
 * `pending` is a parameter rather than something this component works out for
 * itself, because the caller owns the fan-out and only the caller knows when
 * ALL of it has settled. Drawing "you are out of credit" on the first paint of
 * a cold cache and then taking it away a moment later is worse than never
 * drawing it: the customer is left unsure whether they saw it, and this banner
 * is the one thing on the screen that has to be believed.
 *
 * It names every affected application rather than summarising, for the reason
 * the wallets screen refuses to show a single balance — credit is held per
 * application and spent only there, so "a wallet is low" is not something a
 * customer can act on. The tone follows the LOUDEST open crossing, so one
 * critical wallet is not softened by two merely-warned ones.
 */
export function WalletAlertBanner({
  entries,
  pending,
  linkToWallet = true,
  className,
}: {
  entries: WalletAlertEntry[];
  /** True while any read the caller fanned out is still in flight. */
  pending: boolean;
  /** Whether each line offers its own "Add credit" link. Off on the wallet's
   *  own screen, where the top-up panel is already beside the banner and the
   *  link would only lead back to the page the customer is on. */
  linkToWallet?: boolean;
  className?: string;
}) {
  if (pending) {
    return null;
  }
  const open = openAlerts(entries);
  if (open.length === 0) {
    return null;
  }

  const worst = open[0]?.alert.severity ?? "warning";
  const single = open.length === 1 ? open[0] : undefined;

  return (
    <Notice
      tone={alertNoticeTone(worst)}
      title={
        single
          ? `${single.app.name} is running out of credit`
          : `${open.length} wallets are running out of credit`
      }
      icon={<TriangleAlert className="size-4" aria-hidden />}
      className={className}
    >
      <ul className="space-y-2">
        {open.map((entry) => {
          const forecast = alertForecast(entry.alert.detail);
          return (
            <li key={entry.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <span className="font-semibold text-ink-2">{entry.app.name}</span>
                {" — "}
                {/* Both figures, because one without the other is not
                    actionable: a balance alone does not say how far past the
                    line it is, and a threshold alone does not say whether the
                    customer is near it. */}
                {`balance ${formatMeasure(entry.alert.observed)}, at or below the ${formatMeasure(
                  entry.alert.threshold,
                )} alert threshold.`}
                {forecast ? ` ${forecast}` : null}
              </span>
              {linkToWallet ? (
                <Button href={`/wallets/${entry.app.id}`} size="sm" variant="secondary">
                  Add credit
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {linkToWallet ? null : (
        <p className="mt-2">Use the “Add credit” panel on this page to top this wallet up.</p>
      )}
    </Notice>
  );
}

/** Local time, short. Every timestamp on the wire is RFC 3339 UTC. */
export function when(timestamp: string | undefined): string {
  return timestamp ? new Date(timestamp).toLocaleString() : "—";
}
